import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { fetchFastApi } from "../api/fastapi";
import {
  OptroWorkpaperFile,
  OptroWorkpaperFileSchema,
} from "../models/audit/optroSync";

const LinkedWorkpapersResponseSchema = z.object({
  workpapers: z.array(OptroWorkpaperFileSchema),
});

// Each control's last-fetched candidates, keyed by external ref. The owner
// decides its lifetime: the controls import wizard creates one per wizard, so
// reopening the dialog (or going Review -> Select -> Review) shows the list
// already fetched, while a new import, or a new sign-in, starts empty. The
// dialog's "Refresh from Optro" button picks up documents added since.
export interface LinkedWorkpapersCache {
  lists: Map<string, OptroWorkpaperFile[]>;
  // Id of the newest request per ref. Requests for one ref can overlap (the
  // dialog closed and reopened before its first load settled, or a refresh),
  // so only the newest one's response is cached or shown: an older one
  // landing late can't replace a fresher list.
  newestRequestIds: Map<string, number>;
  lastRequestId: number;
}

export function createLinkedWorkpapersCache(): LinkedWorkpapersCache {
  return { lists: new Map(), newestRequestIds: new Map(), lastRequestId: 0 };
}

// The ref's candidates, or null when a newer request for the same ref has
// started since this one (its response is then dropped).
async function fetchLinkedWorkpapers(
  cache: LinkedWorkpapersCache,
  controlExternalRef: string,
): Promise<OptroWorkpaperFile[] | null> {
  const requestId = ++cache.lastRequestId;
  cache.newestRequestIds.set(controlExternalRef, requestId);
  const response = await fetchFastApi({
    method: "GET",
    path: `/controls/external/control-instance/${encodeURIComponent(controlExternalRef)}/workpapers`,
  });
  if (cache.newestRequestIds.get(controlExternalRef) !== requestId) return null;
  const workpapers = LinkedWorkpapersResponseSchema.parse(response).workpapers;
  cache.lists.set(controlExternalRef, workpapers);
  return workpapers;
}

// Lists every workpaper candidate linked to a control (across all its
// tests) for the Change Workpaper dialog, served from `cache` when it holds
// the ref. `refresh` refetches from Optro and replaces the cached list; a
// failed refresh keeps the list already shown. `refreshing` and the failure
// toast belong to the control being refreshed, so switching the dialog to
// another control mid-refresh leaves that one untouched.
export function useOptroLinkedWorkpapers(
  cache: LinkedWorkpapersCache,
  controlExternalRef: string | null,
  enabled: boolean,
): {
  workpapers: OptroWorkpaperFile[];
  loading: boolean;
  refreshing: boolean;
  refresh: () => void;
} {
  const [workpapers, setWorkpapers] = useState<OptroWorkpaperFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshingRef, setRefreshingRef] = useState<string | null>(null);
  // The ref the dialog is showing now, so a refresh that resolves after the
  // dialog moved to another control doesn't touch that control's view.
  const shownRef = useRef<string | null>(null);

  useEffect(() => {
    shownRef.current = enabled ? controlExternalRef : null;
    if (!enabled || !controlExternalRef) {
      setWorkpapers([]);
      setLoading(false);
      return;
    }
    const cached = cache.lists.get(controlExternalRef);
    if (cached) {
      setWorkpapers(cached);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchLinkedWorkpapers(cache, controlExternalRef)
      .then((fetched) => {
        if (!cancelled && fetched) setWorkpapers(fetched);
      })
      .catch((error) => {
        console.error("Failed to load linked workpapers:", error);
        if (!cancelled) setWorkpapers([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cache, enabled, controlExternalRef]);

  const refresh = useCallback(() => {
    if (!controlExternalRef) return;
    const isShown = () => shownRef.current === controlExternalRef;
    setRefreshingRef(controlExternalRef);
    fetchLinkedWorkpapers(cache, controlExternalRef)
      .then((fetched) => {
        if (fetched && isShown()) setWorkpapers(fetched);
      })
      .catch((error) => {
        console.error("Failed to refresh linked workpapers:", error);
        if (isShown()) {
          toast.error(
            "Couldn't refresh from Optro. Showing the last loaded list.",
          );
        }
      })
      .finally(() =>
        setRefreshingRef((current) =>
          current === controlExternalRef ? null : current,
        ),
      );
  }, [cache, controlExternalRef]);

  return {
    workpapers,
    loading,
    refreshing: refreshingRef !== null && refreshingRef === controlExternalRef,
    refresh,
  };
}
